import {APP_INITIALIZER, Injector, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {HttpClient, HttpClientModule, HTTP_INTERCEPTORS} from '@angular/common/http';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {MAT_FORM_FIELD_DEFAULT_OPTIONS} from "@angular/material/form-field";
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {environment} from 'src/environments/environment';
import { GooglePlaceModule } from "ngx-google-places-autocomplete";
// Font Awesome
import {FontAwesomeModule, FaIconLibrary} from '@fortawesome/angular-fontawesome';
import {faSquare, faCheckSquare} from '@fortawesome/free-solid-svg-icons';
import {
  faSquare as farSquare,
  faCheckSquare as farCheckSquare,
  faUser as faUser
} from '@fortawesome/free-regular-svg-icons';
// Modules
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {CoreModule} from "./core/core.module";
import {SharedModule} from "./shared/shared.module";
// client modules
import {DocusignModule} from "./docusign/docusign.module";

import {CustomerIdentificationService, AnswersService} from '../../ApiGen/CustomerVerificationV2';
import {NotificationService} from './shared/services/notification.service';
import {AppSettingsService} from './shared/services/appsettings.service';
import {FacadeService} from "./shared/service-facade/facade.service";
import {LoggerModule, NGXLogger, NgxLoggerLevel} from "ngx-logger";
import {UsernameValidator} from 'src/app/shared/validators/username-validator';
import {FalconApiModule, BASE_PATH as BASE_PATH_FALCON} from 'falcon-api';

import { LeiaApiModule, BASE_PATH as BASE_PATH_LEIA } from "leia-api";
import { SecureApplicationControllerService, ApplicationLogsControllerService, ApplicantControllerService } from "leia-api";

import {SetProductModule} from "./set-product/set-product.module";
import {SkyhopperApiModule, BASE_PATH as BASE_PATH_SKYHOPPER, AddressVerificationControllerService} from "skyhopper-api";

import {StatusTrackerModule} from "./status-tracker/status-tracker.module";
import {SectionStatusService} from "./status-tracker/section-status.service";
import {SignupModule} from "./signup/signup.module";
import {ReviewModule} from "./review/review.module";
import {MaterialModule} from "./shared/material.module";
import {FlexLayoutModule} from "@angular/flex-layout";
import { MatIconModule } from '@angular/material/icon';
import {OverlayModule} from "@angular/cdk/overlay";
import { IConfig, NgxMaskModule } from "ngx-mask";
import { ErrorComponent } from './error/error.component';
import { SuccessComponent } from './success/success.component';
import {ProductGuard} from "./guards/product.guard";
import {SecureSessionGuard} from "./guards/secure-session.guard";
import {ExistingAppGuard} from "./guards/existing-app.guard";
import {ProcessService} from "./process/process.service";
import { MaintenanceComponent } from "./maintenance/maintenance.component";
import { initializeApp } from './core/app_initializer';
import {CookieService} from "ngx-cookie-service";
import { SessionStorageService } from './shared/services/session-storage.service';
import { BearerTokenInterceptor } from './core/bearer-token.interceptor';
import { NgIdleKeepaliveModule } from "@ng-idle/keepalive";
import {BankingCenterComponent} from "./banking-center/banking-center.component";
import {BranchComponent} from "./branch/branch.component";
import {BackButtonGuard} from "./guards/back-button.guard";
import {SuggestedAddressService} from "./shared/services/suggested-address.service";
import {NgxFaviconModule} from "ngx-favicon";
import {AvailableCustomFavicons, customFavicons} from "./core/favicon.config";
import {JointApplicationMaintenanceModule} from "./joint-application-maintenance/joint-application-maintenance.module";
import {LoginDetailsModule} from "./login-details/login-details.module";
import { ProcessModule } from './process/process.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppStoreBannerComponent } from './shared/components/app-store-banner/app-store-banner.component';
import { TechnicalIssueMessageComponent } from './success/technical-issue-message/technical-issue-message.component';
import { ApplicationIdleService } from './core/services/application-idle.service';
import {ManualRoutingGuard} from "./guards/manual-routing-guard";
import { QueueInterceptor } from './core/queue.interceptor';
import { CompanyIdentifierComponent } from './employee-benefit/company-identifier/company-identifier.component';
import { ProductSelectionComponent } from './employee-benefit/product-selection/product-selection.component';
import { ActionsComponent } from './actions/actions.component';
import { DocumentCaptureWarningDialogComponent } from './actions/dialogs/document-capture-warining-dialog/document-capture-warning-dialog.component';
import { CancelDialogComponent } from './actions/dialogs/cancel-dialog/cancel-dialog.component';
import { JointApplicationWarningDialogComponent } from './actions/dialogs/joint-application-warning-dialog/joint-application-warning-dialog.component';
import { JointInvitationSuccessDialogComponent } from './actions/dialogs/joint-invitation-success-dialog/joint-invitation-success-dialog.component';
import { OfflineModeInterceptor } from './core/offline-mode.interceptor';
import { NgxPlaidLinkModule } from './plaid/public_api';

export let AppInjector: Injector;
const maskConfig: Partial<IConfig> = { };

@NgModule({
  declarations: [
    AppComponent,
    ErrorComponent,
    SuccessComponent,
    TechnicalIssueMessageComponent,
    MaintenanceComponent,
    BankingCenterComponent,
    BranchComponent,
    AppStoreBannerComponent,
    CompanyIdentifierComponent,
    ProductSelectionComponent,
    ActionsComponent,
    DocumentCaptureWarningDialogComponent,
    CancelDialogComponent,
    JointApplicationWarningDialogComponent,
    JointInvitationSuccessDialogComponent,
  ],
  imports: [
    JointApplicationMaintenanceModule,
    GooglePlaceModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FontAwesomeModule,
    NgbModule,
    CoreModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    FalconApiModule,
    LeiaApiModule,
    LoggerModule.forRoot({
      level: NgxLoggerLevel.DEBUG,
      serverLogLevel: NgxLoggerLevel.DEBUG,
    }),
    NgxFaviconModule.forRoot<AvailableCustomFavicons>({
      faviconElementId: 'favicon',
      defaultUrl: 'favicon.ico',
      custom: customFavicons,
    }),
    DocusignModule,
    SkyhopperApiModule,
    SetProductModule,
    StatusTrackerModule,
    SignupModule,
    ReviewModule,
    ProcessModule,
    MaterialModule,
    FlexLayoutModule,
    MatIconModule,
    OverlayModule,
    NgxMaskModule.forRoot(maskConfig),
    NgIdleKeepaliveModule.forRoot(),
    LoginDetailsModule,
    NgxPlaidLinkModule
  ],
  providers: [SecureApplicationControllerService,CustomerIdentificationService, NotificationService, UsernameValidator,
    AppSettingsService, AnswersService, ApplicationIdleService,
    ApplicationLogsControllerService, ApplicantControllerService, FacadeService, AddressVerificationControllerService,
    SectionStatusService, ProcessService, SuggestedAddressService,
    ProductGuard, SecureSessionGuard, ExistingAppGuard, BackButtonGuard, ManualRoutingGuard,
    SessionStorageService, CookieService,
    {provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: {appearance: "fill"}},
    {provide: BASE_PATH_FALCON, useValue: environment.api_base_url_falcon},
    {provide: BASE_PATH_LEIA, useValue: environment.api_base_url_leia},
    {provide: BASE_PATH_SKYHOPPER, useValue: environment.api_base_url_skyhopper},
    {provide: APP_INITIALIZER, useFactory: initializeApp, multi: true, deps: [CookieService, SessionStorageService, HttpClient]},
    [
      {provide: HTTP_INTERCEPTORS, useClass: BearerTokenInterceptor, multi: true},
      {provide: HTTP_INTERCEPTORS, useClass: QueueInterceptor, multi: true},
      {provide: HTTP_INTERCEPTORS, useClass: OfflineModeInterceptor, multi: true},
    ],
  ],
  bootstrap: [AppComponent]
})

export class AppModule {
  constructor(private library: FaIconLibrary,
              private logger: NGXLogger,
              private injector: Injector) {
    AppInjector = this.injector;
    library.addIcons(faSquare, faCheckSquare, farSquare, farCheckSquare, faUser);
    this.logger.debug(' Mando Base Api :' + environment.api_base_url_mando +
      ' Falcon Base Api :' + environment.api_base_url_falcon +
      ' Leia Base Api :' + environment.api_base_url_leia +
      ' Skyhopper Base Api :' + environment.api_base_url_skyhopper +
      ' production? ' + environment.production +
      ' env: ' + environment.env);
  }
}